import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, useMotionValue, useTransform, animate } from "framer-motion";
import { useI18n } from "@/i18n";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Counter({ to, suffix = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const value = useMotionValue(0);
  const rounded = useTransform(value, (latest) => Math.floor(latest).toLocaleString());

  useEffect(() => {
    if (inView) {
      const controls = animate(value, to, {
        duration: 2.2,
        delay,
        ease: [0.16, 1, 0.3, 1],
      });
      return controls.stop;
    }
  }, [inView, to, value, delay]);

  return (
    <div ref={ref} className="flex items-baseline">
      <motion.span className="font-display font-extralight text-6xl md:text-7xl lg:text-8xl tracking-tighter text-gradient-silver">
        {rounded}
      </motion.span>
      <span className="font-display font-light text-3xl md:text-4xl text-white/60 ml-1">
        {suffix}
      </span>
    </div>
  );
}

export default function TrustSection() {
  const { t } = useI18n();
  const [stats, setStats] = useState({
    projects_completed: 120,
    brands_helped: 48,
    content_produced: 2400,
    client_satisfaction: 100,
  });

  useEffect(() => {
    axios
      .get(`${API}/stats`)
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, []);

  const values = [
    stats.projects_completed,
    stats.brands_helped,
    stats.content_produced,
    stats.client_satisfaction,
  ];

  return (
    <section
      id="trust"
      data-testid="trust-section"
      className="relative bg-black py-28 md:py-40 overflow-hidden"
    >
      <div className="absolute inset-0 dot-grid opacity-30" />
      <div className="absolute inset-0 radial-fade" />

      <div className="relative container mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="flex items-center gap-3 mb-10"
        >
          <span className="w-10 h-px bg-lux" />
          <span className="text-[11px] uppercase tracking-[0.3em] text-lux">
            {t.trust.label}
          </span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="font-display text-3xl sm:text-4xl lg:text-5xl font-light max-w-3xl tracking-tight mb-20 text-balance"
        >
          {t.trust.title}
        </motion.h2>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-14 gap-x-8">
          {t.trust.items.map((it, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.8, delay: i * 0.1 }}
              data-testid={`trust-stat-${i}`}
              className="border-l border-white/10 pl-6"
            >
              <Counter to={values[i]} suffix={it.suffix} delay={i * 0.15} />
              <div className="text-xs sm:text-sm text-white/50 uppercase tracking-[0.18em] mt-4">
                {it.k}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
